/**
 * Percentage splits for the AI's bill classification (ALC-4641): which percentages are valid, and
 * how a total in cents divides across them.
 *
 * Money is compared and added as integer cents and percentages as integer basis points (1% = 100),
 * never as floats. The one place a `number` of reais meets cents is `allocateSplitAmounts`, which
 * converts the bill total to whole cents once and divides it with integers.
 */

/** Dimensions the AI may split when a vendor instruction asks for a rateio. */
export const AI_SPLIT_DIMENSIONS = ['costCenters', 'departments', 'managerialAccounts'] as const;

export type AiSplitDimension = (typeof AI_SPLIT_DIMENSIONS)[number];

/**
 * Stable reasons the AI's split for one dimension is refused. Never match on message text: the
 * bill panel and the history map these codes to copy (pt-BR and en).
 */
export const AiSplitRejectionCode = {
  /** A percent is not a positive number with at most two decimals, or the list is empty. */
  PERCENT_INVALID: 'AI_SPLIT_PERCENT_INVALID',
  /** The percentages of the dimension do not add up to exactly 100. */
  PERCENT_SUM_INVALID: 'AI_SPLIT_PERCENT_SUM_INVALID',
  /** A target is not an active, selectable option of this entity (unknown, inactive or NP). */
  TARGET_UNKNOWN: 'AI_SPLIT_TARGET_UNKNOWN',
  /** The same target appears twice in the dimension. */
  TARGET_DUPLICATE: 'AI_SPLIT_TARGET_DUPLICATE',
} as const;

export type AiSplitRejectionCode = (typeof AiSplitRejectionCode)[keyof typeof AiSplitRejectionCode];

/** pt-BR name of each splittable dimension, for the notes the classification pipeline writes. */
const AI_SPLIT_DIMENSION_LABEL_PT: Record<AiSplitDimension, string> = {
  costCenters: 'centro de custo',
  departments: 'departamento',
  managerialAccounts: 'conta gerencial',
};

const AI_SPLIT_REJECTION_REASON_PT: Record<AiSplitRejectionCode, string> = {
  [AiSplitRejectionCode.PERCENT_INVALID]:
    'cada percentual deve ser maior que zero, com no máximo duas casas decimais',
  [AiSplitRejectionCode.PERCENT_SUM_INVALID]: 'os percentuais não somam exatamente 100',
  [AiSplitRejectionCode.TARGET_UNKNOWN]:
    'uma das opções não existe, está inativa ou não pode receber lançamentos',
  [AiSplitRejectionCode.TARGET_DUPLICATE]: 'a mesma opção aparece mais de uma vez',
};

/**
 * The note written on the bill's classification when the AI's split for a dimension is refused.
 * It carries the stable code so a report can find it; the bill panel shows the localised copy for
 * the same code instead of this text.
 */
export function describeAiSplitRejectionPt(
  dimension: AiSplitDimension,
  code: AiSplitRejectionCode,
): string {
  return `Rateio da IA não aplicado em ${AI_SPLIT_DIMENSION_LABEL_PT[dimension]} (${code}): ${AI_SPLIT_REJECTION_REASON_PT[code]}.`;
}

/**
 * Stable reasons the AI's whole classification could not be saved (ALC-4641). The save is refused
 * by the same validator a person's save goes through; the bill keeps the code so a report can find
 * it, and the panel and the history show the localised copy for it.
 */
export const AiSaveRefusalCode = {
  /** The allocated amounts of a dimension do not add up to the bill amount. */
  AMOUNT_TOTAL_INVALID: 'AI_CLASSIFICATION_AMOUNT_TOTAL_INVALID',
} as const;

export type AiSaveRefusalCode = (typeof AiSaveRefusalCode)[keyof typeof AiSaveRefusalCode];

const AI_SAVE_REFUSAL_REASON_PT: Record<AiSaveRefusalCode, string> = {
  [AiSaveRefusalCode.AMOUNT_TOTAL_INVALID]: 'os valores alocados não somam o valor da conta',
};

/** The sentence the bill history gives a classification the AI proposed and the system refused. */
export function describeAiSaveRefusalPt(code: AiSaveRefusalCode): string {
  return `Classificação da IA não aplicada (${code}): ${AI_SAVE_REFUSAL_REASON_PT[code]}.`;
}

/**
 * Why the AI did not copy the history of an instructed vendor's last bill (ALC-4641). The vendor's
 * instruction wins and the AI classifies the bill on its own; the history of a bill whose
 * classification is not one rateio on the bill (per line item, or a custom breakdown) has no
 * dimension to fill from, and copying it whole would put the history over the instruction. The
 * bill keeps the code and the reference bill so a report can find it.
 */
export const AiSkippedReferenceCode = {
  /** The reference bill classifies per line item or with a custom breakdown. */
  PATTERN_NOT_APPLIED: 'AI_INSTRUCTION_SKIPPED_REFERENCE_PATTERN',
} as const;

export type AiSkippedReferenceCode =
  (typeof AiSkippedReferenceCode)[keyof typeof AiSkippedReferenceCode];

/** The reference bill patterns the AI does not copy when a vendor instruction applies. */
export const AI_SKIPPED_REFERENCE_PATTERNS = ['LINE_ITEM_LEVEL', 'CUSTOM_BREAKDOWN'] as const;

export type AiSkippedReferencePattern = (typeof AI_SKIPPED_REFERENCE_PATTERNS)[number];

const AI_SKIPPED_REFERENCE_PATTERN_PT: Record<AiSkippedReferencePattern, string> = {
  LINE_ITEM_LEVEL: 'classificação por item',
  CUSTOM_BREAKDOWN: 'rateio personalizado',
};

/**
 * The note written on the bill's classification (and in its history) when the history of the
 * reference bill was not applied. It carries the stable code and names the reference bill; the
 * bill panel shows the localised copy for the same code instead of this text.
 */
export function describeAiSkippedReferencePt(
  code: AiSkippedReferenceCode,
  pattern: AiSkippedReferencePattern,
  referenceBillId: string,
): string {
  return `A IA seguiu a instrução do fornecedor e não copiou o histórico da conta ${referenceBillId} (${code}): ela usa ${AI_SKIPPED_REFERENCE_PATTERN_PT[pattern]}.`;
}

/**
 * Why the AI did not carry the PIS/COFINS credit flags of the vendor's last bill to the new one
 * (ALC-4641). The flags (generates PIS credit, generates COFINS credit, credit type) are a tax
 * position, and a wrong credit costs more than a missed one: when the vendor instruction chose a
 * different GL account than that bill used, the flags are not copied; when the copy was tried and
 * the system refused it, or failed, the bill says so. The bill keeps the code and the reference
 * bill so a report can find it, and the panel and the history show the localised copy for it.
 */
export const AiCreditFlagsNoteCode = {
  /** The instruction chose a GL account other than the reference bill's, so no flag was copied. */
  NOT_COPIED: 'AI_INSTRUCTION_CREDIT_FLAGS_NOT_COPIED',
  /** The copy was tried and refused or failed, so the bill may be missing its credit flags. */
  COPY_FAILED: 'AI_INSTRUCTION_CREDIT_FLAGS_COPY_FAILED',
} as const;

export type AiCreditFlagsNoteCode =
  (typeof AiCreditFlagsNoteCode)[keyof typeof AiCreditFlagsNoteCode];

const AI_CREDIT_FLAGS_NOTE_REASON_PT: Record<AiCreditFlagsNoteCode, string> = {
  [AiCreditFlagsNoteCode.NOT_COPIED]: 'a instrução escolheu outra conta contábil',
  [AiCreditFlagsNoteCode.COPY_FAILED]: 'a cópia não foi concluída, confira os créditos desta conta',
};

/**
 * The note written on the bill's classification (and in its history) when the credit flags of the
 * reference bill were not carried over. It carries the stable code and names the reference bill;
 * the bill panel shows the localised copy for the same code instead of this text.
 */
export function describeAiCreditFlagsNotePt(
  code: AiCreditFlagsNoteCode,
  referenceBillId: string,
): string {
  return `A IA não copiou os créditos de PIS e COFINS da conta ${referenceBillId} (${code}): ${AI_CREDIT_FLAGS_NOTE_REASON_PT[code]}.`;
}

const FULL_BASIS_POINTS = 10_000;
const BASIS_POINT_EPSILON = 1e-6;

/**
 * A percent as integer basis points, or null when it is not a positive number up to 100 with at
 * most two decimals (so 33.333 is refused rather than silently rounded).
 */
export function percentToBasisPoints(percent: number): number | null {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) return null;
  const scaled = percent * 100;
  const basisPoints = Math.round(scaled);
  if (Math.abs(scaled - basisPoints) > BASIS_POINT_EPSILON) return null;
  if (basisPoints < 1 || basisPoints > FULL_BASIS_POINTS) return null;
  return basisPoints;
}

export type SplitPercentValidation =
  { ok: true; basisPoints: number[] } | { ok: false; code: AiSplitRejectionCode };

/** Percentages of one dimension are valid when each is well formed and they add up to 100. */
export function validateSplitPercents(percents: number[]): SplitPercentValidation {
  if (percents.length === 0) return { ok: false, code: AiSplitRejectionCode.PERCENT_INVALID };
  const basisPoints: number[] = [];
  for (const percent of percents) {
    const converted = percentToBasisPoints(percent);
    if (converted === null) return { ok: false, code: AiSplitRejectionCode.PERCENT_INVALID };
    basisPoints.push(converted);
  }
  const total = basisPoints.reduce((sum, value) => sum + value, 0);
  if (total !== FULL_BASIS_POINTS) {
    return { ok: false, code: AiSplitRejectionCode.PERCENT_SUM_INVALID };
  }
  return { ok: true, basisPoints };
}

/**
 * Divides a total across weights in whole cents. Each share is rounded down; the cents left over
 * go to the split with the largest weight, and to the first of them in model order on a tie. The
 * shares always add up to the total, whatever the weights add up to (3 x 33,33% on R$ 100,00 gives
 * 33,34 / 33,33 / 33,33). Weights are basis points; callers validate them first.
 */
function allocateCentsByWeight(totalCents: number, weights: number[]): number[] {
  if (weights.length === 0) return [];
  const sign = totalCents < 0 ? -1n : 1n;
  const absolute = BigInt(Math.abs(totalCents));
  const weightTotal = weights.reduce((sum, weight) => sum + BigInt(weight), 0n);
  if (weightTotal === 0n) return weights.map(() => 0);

  const shares = weights.map((weight) => (absolute * BigInt(weight)) / weightTotal);
  const allocated = shares.reduce((sum, share) => sum + share, 0n);
  const remainder = absolute - allocated;

  let largest = 0;
  for (let index = 1; index < weights.length; index += 1) {
    if ((weights[index] ?? 0) > (weights[largest] ?? 0)) largest = index;
  }
  shares[largest] = (shares[largest] ?? 0n) + remainder;
  return shares.map((share) => Number(share * sign));
}

/**
 * The amount of each split, in reais with two decimals, for a total and the splits' percentages.
 * The total is taken to whole cents first; the amounts add up to it exactly.
 */
export function allocateSplitAmounts(totalAmount: number, percents: number[]): number[] {
  const weights = percents.map((percent) => Math.round(percent * 100));
  return allocateCentsByWeight(Math.round(totalAmount * 100), weights).map((cents) => cents / 100);
}
